import { Plan, User } from '@/api/models';
import { Button } from '@/components/ui/Button';
import { Drawer } from '@/components/ui/Drawer';
import { TabNavigation } from '@/components/ui/TabNavigation';
import { UserFormData } from '@/pages/users/hooks/useUserMutations';
import {
  PlanFormData,
  UserPlanMutationsActions,
} from '@/pages/users/hooks/useUserPlanMutations';
import {
  UserPlansActions,
  UserPlansState,
} from '@/pages/users/hooks/useUserPlans';
import { mdiDelete, mdiPencil } from '@mdi/js';
import * as React from 'react';
import { UserForm } from './UserForm';
import { UserPlanForm } from './UserPlanForm';
import { UserPlansTab } from './UserPlansTab';

export interface UserDrawerViewProps {
  // State
  isOpen: boolean;
  user: User | null;
  mode: 'view' | 'create' | 'edit';

  // Loading
  isLoading: boolean;
  isFormLoading: boolean;

  // Plans state
  plansState?: UserPlansState;
  plansActions?: UserPlansActions;
  planMutationActions?: UserPlanMutationsActions;
  isPlanDeleting?: boolean;
  isPlanFormLoading?: boolean;

  // Actions
  onClose: () => void;
  onEdit: (user: User) => void;
  onDelete: (user: User) => void;
  onFormSubmit: (data: UserFormData) => Promise<void>;
  onPlanFormSubmit?: (data: PlanFormData) => Promise<void>;
}

type DrawerTab = 'details' | 'plans';
type PlanMode = 'list' | 'create' | 'edit';

export const UserDrawerView: React.FC<UserDrawerViewProps> = ({
  // State
  isOpen,
  user,
  mode,

  // Loading
  isLoading,
  isFormLoading,

  // Plans state
  plansState,
  plansActions,
  planMutationActions,
  isPlanDeleting = false,
  isPlanFormLoading = false,

  // Actions
  onClose,
  onEdit,
  onDelete,
  onFormSubmit,
  onPlanFormSubmit,
}) => {
  const [activeTab, setActiveTab] = React.useState<DrawerTab>('details');
  const [planMode, setPlanMode] = React.useState<PlanMode>('list');
  const [selectedPlan, setSelectedPlan] = React.useState<Plan | null>(null);

  // Reset tab when drawer closes or user changes
  React.useEffect(() => {
    if (!isOpen) {
      setActiveTab('details');
      setPlanMode('list');
      setSelectedPlan(null);
    }
  }, [isOpen]);

  React.useEffect(() => {
    if (mode !== 'view') {
      setActiveTab('details');
      setPlanMode('list');
      setSelectedPlan(null);
    }
  }, [mode]);

  const getTitle = () => {
    if (planMode === 'create') return 'Create Plan';
    if (planMode === 'edit') return 'Edit Plan';

    switch (mode) {
      case 'create':
        return 'Create New User';
      case 'edit':
        return user ? `Edit ${user.firstName} ${user.lastName}` : 'Edit User';
      case 'view':
      default:
        return user ? `${user.firstName} ${user.lastName}` : 'User Details';
    }
  };

  const handleCreatePlan = () => {
    setPlanMode('create');
    setSelectedPlan(null);
  };

  const handleEditPlan = (plan: Plan) => {
    setPlanMode('edit');
    setSelectedPlan(plan);
  };

  const handlePlanFormCancel = () => {
    setPlanMode('list');
    setSelectedPlan(null);
  };

  const handlePlanFormSubmit = async (data: PlanFormData) => {
    if (onPlanFormSubmit) {
      await onPlanFormSubmit(data);
      setPlanMode('list');
      setSelectedPlan(null);
    }
  };

  const tabs = [
    { id: 'details', label: 'Details' },
    { id: 'plans', label: 'Plans', count: plansState?.total },
  ];

  if (isLoading) {
    return (
      <Drawer isOpen={isOpen} onClose={onClose} title='Loading...' width='lg'>
        <div className='p-6 flex items-center justify-center'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600'></div>
        </div>
      </Drawer>
    );
  }

  // Show plan form when creating/editing a plan
  if (
    mode === 'view' &&
    user &&
    (planMode === 'create' || planMode === 'edit')
  ) {
    return (
      <Drawer isOpen={isOpen} onClose={onClose} title={getTitle()} width='lg'>
        <UserPlanForm
          plan={planMode === 'edit' ? selectedPlan : null}
          onSubmit={handlePlanFormSubmit}
          onCancel={handlePlanFormCancel}
          loading={isPlanFormLoading}
        />
      </Drawer>
    );
  }

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title={getTitle()} width='lg'>
      {mode === 'view' && user && (
        <>
          {/* Tab Navigation */}
          <TabNavigation
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={tab => setActiveTab(tab as DrawerTab)}
            size='sm'
          />

          {/* Details Tab */}
          {activeTab === 'details' && (
            <div className='p-6 space-y-6'>
              {/* User Header */}
              <div className='flex items-center space-x-4 pb-6 border-b border-gray-200'>
                <div className='w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-2xl font-bold'>
                  {(user.firstName?.[0] || '') + (user.lastName?.[0] || '')}
                </div>
                <div className='flex-1'>
                  <h3 className='text-xl font-semibold text-gray-900'>
                    {user.firstName} {user.lastName}
                  </h3>
                  <p className='text-gray-500'>{user.email}</p>
                  <div className='mt-2 flex items-center space-x-2 flex-wrap'>
                    {user.roles && user.roles.length > 0 ? (
                      user.roles.map((role: string, index: number) => (
                        <span
                          key={index}
                          className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700'
                        >
                          {role
                            .replace('_', ' ')
                            .replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      ))
                    ) : (
                      <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700'>
                        User
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* User Details */}
              <div className='grid grid-cols-1 gap-6'>
                <div className='bg-gray-50 rounded-lg p-4'>
                  <h4 className='text-sm font-medium text-gray-700 mb-3'>
                    Account Information
                  </h4>
                  <dl className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    <div>
                      <dt className='text-xs text-gray-500'>User ID</dt>
                      <dd className='text-sm font-mono text-gray-900'>
                        {user.id}
                      </dd>
                    </div>
                    <div className='overflow-hidden'>
                      <dt className='text-xs text-gray-500'>Auth ID</dt>
                      <dd className='text-sm font-mono text-gray-900 break-all'>
                        {user.authId}
                      </dd>
                    </div>
                    <div>
                      <dt className='text-xs text-gray-500'>Email</dt>
                      <dd className='text-sm text-gray-900'>
                        {user.email || 'No email'}
                      </dd>
                    </div>
                    <div>
                      <dt className='text-xs text-gray-500'>Phone</dt>
                      <dd className='text-sm text-gray-900'>
                        {user.phone || 'No phone'}
                      </dd>
                    </div>
                    <div>
                      <dt className='text-xs text-gray-500'>Created</dt>
                      <dd className='text-sm text-gray-900'>
                        {user.createdAt
                          ? new Date(user.createdAt).toLocaleDateString(
                              'en-US',
                              {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              },
                            )
                          : 'N/A'}
                      </dd>
                    </div>
                  </dl>
                </div>

                {/* Quick Actions */}
                <div className='bg-gray-50 rounded-lg p-4'>
                  <h4 className='text-sm font-medium text-gray-700 mb-3'>
                    Quick Actions
                  </h4>
                  <div className='flex flex-wrap gap-2'>
                    <Button
                      icon={mdiPencil}
                      label='Edit User'
                      color='info'
                      small
                      onClick={() => onEdit(user)}
                    />
                    <Button
                      icon={mdiDelete}
                      label='Delete User'
                      color='danger'
                      small
                      outline
                      onClick={() => onDelete(user)}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Plans Tab */}
          {activeTab === 'plans' &&
            plansState &&
            plansActions &&
            planMutationActions && (
              <UserPlansTab
                user={user}
                plansState={plansState}
                plansActions={plansActions}
                mutationActions={planMutationActions}
                isDeleting={isPlanDeleting}
                onCreatePlan={handleCreatePlan}
                onEditPlan={handleEditPlan}
              />
            )}
        </>
      )}

      {(mode === 'create' || mode === 'edit') && (
        <UserForm
          user={user}
          onSubmit={onFormSubmit}
          onCancel={onClose}
          loading={isFormLoading}
        />
      )}
    </Drawer>
  );
};
